/**
 * QuestionnaireResults — aggregated response summary view.
 * Full implementation with per-question visualizations, Common Ground section,
 * filter toggle, and CSV export.
 */
import React, { useState, useMemo, useCallback } from 'react';
import type {
  QuestionnaireStep,
  QuestionnaireResponse,
  AggregatedResponses,
  QuestionAggregation,
} from '@/types/pm-types';
import { aggregateResponses, exportResponsesCsv } from '@/utils/questionnaireAggregation';

export interface QuestionnaireResultsProps {
  steps: QuestionnaireStep[];
  responses: QuestionnaireResponse[];
  aggregated: AggregatedResponses | null;
  accentColor?: string;
}

/** Find options selected by a majority of respondents */
function findCommonPreferences(
  aggregation: QuestionAggregation,
  totalRespondents: number,
  minAgreement = 0.5,
): string[] {
  if (!aggregation.valueCounts) return [];
  return Object.entries(aggregation.valueCounts)
    .filter(([, count]) => count / totalRespondents >= minAgreement)
    .sort((a, b) => b[1] - a[1])
    .map(([val]) => val);
}

/** CSS bar for horizontal bar charts */
const Bar: React.FC<{ label: string; count: number; max: number; color: string }> = ({
  label, count, max, color,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
    <span style={{ width: 100, textAlign: 'right', color: '#4b5563', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 16, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: max > 0 ? `${(count / max) * 100}%` : '0%',
        background: color,
        borderRadius: 4,
        transition: 'width 0.3s ease',
        minWidth: count > 0 ? 4 : 0,
      }} />
    </div>
    <span style={{ width: 28, fontSize: 12, color: '#6b7280' }}>{count}</span>
  </div>
);

const QuestionResult: React.FC<{
  agg: QuestionAggregation;
  totalRespondents: number;
  accentColor: string;
}> = ({ agg, totalRespondents, accentColor }) => {
  const maxCount = agg.valueCounts
    ? Math.max(...Object.values(agg.valueCounts), 1)
    : 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>{agg.questionLabel}</div>

      {(agg.type === 'single-select' || agg.type === 'multi-select') && agg.valueCounts && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {Object.entries(agg.valueCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([val, count]) => (
              <Bar key={val} label={val} count={count} max={maxCount} color={accentColor} />
            ))}
        </div>
      )}

      {agg.type === 'rating' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 24, color: '#f59e0b' }}>
            {'★'.repeat(Math.round(agg.average || 0))}
            {'☆'.repeat(5 - Math.round(agg.average || 0))}
          </span>
          <span style={{ fontSize: 14, color: '#6b7280' }}>
            {(agg.average || 0).toFixed(1)} avg
          </span>
          {agg.distribution && (
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
              ({totalRespondents} response{totalRespondents !== 1 ? 's' : ''})
            </span>
          )}
        </div>
      )}

      {agg.type === 'boolean' && agg.valueCounts && (
        <div style={{ display: 'flex', gap: 16, fontSize: 14 }}>
          <span style={{ color: '#10b981' }}>Yes: {agg.valueCounts['true'] || 0}</span>
          <span style={{ color: '#ef4444' }}>No: {agg.valueCounts['false'] || 0}</span>
        </div>
      )}

      {agg.type === 'number' && (
        <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#4b5563' }}>
          {agg.distribution && (
            <>
              <span>Min: {agg.distribution['min']}</span>
              <span>Avg: {(agg.average || 0).toFixed(1)}</span>
              <span>Max: {agg.distribution['max']}</span>
            </>
          )}
        </div>
      )}

      {agg.type === 'text' && (
        <div style={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {agg.responses.map((resp, i) => (
            <div key={i} style={{
              padding: '6px 10px',
              background: '#f9fafb',
              borderRadius: 4,
              fontSize: 13,
              color: '#374151',
            }}>
              {String(resp)}
            </div>
          ))}
        </div>
      )}

      {agg.type === 'date' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {agg.responses.map((resp, i) => (
            <span key={i} style={{
              padding: '4px 8px',
              background: '#f0fdf4',
              borderRadius: 4,
              fontSize: 12,
              color: '#166534',
            }}>
              {String(resp)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

type FilterMode = 'all' | 'completed' | 'in_progress';

export const QuestionnaireResults: React.FC<QuestionnaireResultsProps> = ({
  steps,
  responses,
  aggregated,
  accentColor = '#2563eb',
}) => {
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // Filter responses based on selected mode
  const filteredResponses = useMemo(() => {
    if (filterMode === 'all') return responses;
    return responses.filter((r) => r.status === filterMode);
  }, [responses, filterMode]);

  // Re-aggregate with filtered responses
  const displayAggregated = useMemo(() => {
    if (filterMode === 'all') return aggregated;
    if (filteredResponses.length === 0) return null;
    return aggregateResponses(filteredResponses, steps);
  }, [filterMode, aggregated, filteredResponses, steps]);

  const handleExportCsv = useCallback(() => {
    const csv = exportResponsesCsv(filteredResponses, steps);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `questionnaire-responses-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredResponses, steps]);

  if (!aggregated || responses.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 32,
        color: '#9ca3af',
      }}>
        <span style={{ fontSize: 32 }}>📊</span>
        <span style={{ fontSize: 14 }}>No responses yet</span>
        <span style={{ fontSize: 12 }}>Responses will appear here once collected</span>
      </div>
    );
  }

  const totalRespondents = displayAggregated?.totalRespondents || 0;
  const completedCount = displayAggregated?.completedCount || 0;

  // Build common preferences section
  const commonItems: { label: string; values: string[] }[] = [];
  if (displayAggregated) {
    for (const [, agg] of Object.entries(displayAggregated.perQuestion)) {
      if (agg.type === 'single-select' || agg.type === 'multi-select') {
        const common = findCommonPreferences(agg, totalRespondents);
        if (common.length > 0) {
          commonItems.push({ label: agg.questionLabel, values: common });
        }
      }
    }
  }

  const filterBtn = (mode: FilterMode, label: string) => (
    <button
      type="button"
      onClick={() => setFilterMode(mode)}
      style={{
        padding: '4px 10px',
        fontSize: 12,
        border: `1px solid ${filterMode === mode ? accentColor : '#d1d5db'}`,
        borderRadius: 4,
        background: filterMode === mode ? accentColor : '#fff',
        color: filterMode === mode ? '#fff' : '#6b7280',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      height: '100%',
      overflowY: 'auto',
      padding: '4px 0',
    }}>
      {/* Header with filter and export */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '0 4px',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
            Response Summary
          </span>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {completedCount} of {totalRespondents} respondent{totalRespondents !== 1 ? 's' : ''} completed
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {filterBtn('all', 'All')}
          {filterBtn('completed', 'Completed')}
          {filterBtn('in_progress', 'In Progress')}
          <button
            type="button"
            onClick={handleExportCsv}
            style={{
              padding: '4px 10px',
              fontSize: 12,
              border: '1px solid #d1d5db',
              borderRadius: 4,
              background: '#fff',
              color: '#374151',
              cursor: 'pointer',
              marginLeft: 4,
            }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Common Ground */}
      {commonItems.length > 0 && (
        <div style={{
          padding: 12,
          background: '#f0fdf4',
          borderRadius: 8,
          border: '1px solid #bbf7d0',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#166534', marginBottom: 8 }}>
            Common Ground
          </div>
          {commonItems.map((item) => (
            <div key={item.label} style={{ fontSize: 13, color: '#166534', marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>{item.label}:</span>{' '}
              {item.values.join(', ')}
            </div>
          ))}
        </div>
      )}

      {/* Per-question results */}
      {displayAggregated && steps.flatMap((step) =>
        step.questions.map((q) => {
          const agg = displayAggregated.perQuestion[q.id];
          if (!agg) return null;
          return (
            <QuestionResult
              key={q.id}
              agg={agg}
              totalRespondents={totalRespondents}
              accentColor={accentColor}
            />
          );
        }),
      )}
    </div>
  );
};

QuestionnaireResults.displayName = 'QuestionnaireResults';
